import { create } from 'zustand';
import {
  buildEditorTabId,
  defaultTabTitle,
  type EditorAccessMode,
  type EditorTab,
  type EditorTabKind,
  type EditorWorkspacePane
} from './editorWorkspaceIds';

export type OpenTabInput = {
  kind: EditorTabKind;
  mountId: string;
  path: string;
  title?: string;
  mediaKind?: 'image' | 'video';
  accessMode?: EditorAccessMode;
};

export interface EditorWorkspaceState {
  tabs: EditorTab[];
  activeTabId: string | null;
  split: boolean;
  focusedPane: EditorWorkspacePane;
  openTab: (input: OpenTabInput) => string;
  focusTab: (tabId: string) => void;
  focusPane: (pane: EditorWorkspacePane) => void;
  closeTab: (tabId: string) => string | null;
  closeOthers: (tabId: string) => string | null;
  closeToTheRight: (tabId: string) => string | null;
  closeAll: () => null;
  splitRight: (tabId: string) => void;
  moveToPane: (tabId: string, pane: EditorWorkspacePane) => void;
  unsplit: () => void;
  setTabDirty: (tabId: string, dirty: boolean) => void;
}

export const INITIAL_EDITOR_WORKSPACE_STATE = {
  tabs: [] as EditorTab[],
  activeTabId: null as string | null,
  split: false,
  focusedPane: 'primary' as EditorWorkspacePane
};

export function tabsInPane(tabs: EditorTab[], pane: EditorWorkspacePane): EditorTab[] {
  return tabs.filter((tab) => tab.pane === pane);
}

export function resetEditorWorkspaceStore(): void {
  useEditorWorkspaceStore.setState({ ...INITIAL_EDITOR_WORKSPACE_STATE });
}

function neighborTabId(tabs: EditorTab[], tabId: string): string | null {
  const tab = tabs.find((item) => item.id === tabId);
  if (!tab) return tabs[0]?.id ?? null;
  const same = tabsInPane(tabs, tab.pane);
  const index = same.findIndex((item) => item.id === tabId);
  return same[index + 1]?.id ?? same[index - 1]?.id ?? null;
}

function collapseEmptyPanes(
  tabs: EditorTab[],
  activeTabId: string | null,
  split: boolean
): Pick<EditorWorkspaceState, 'tabs' | 'split' | 'focusedPane' | 'activeTabId'> {
  const primary = tabsInPane(tabs, 'primary');
  const secondary = tabsInPane(tabs, 'secondary');

  if (!split || secondary.length === 0) {
    const nextTabs = tabs.map((tab) => ({ ...tab, pane: 'primary' as const }));
    const nextActive =
      (activeTabId && nextTabs.some((tab) => tab.id === activeTabId) ? activeTabId : null) ??
      nextTabs[0]?.id ??
      null;
    return {
      tabs: nextTabs,
      split: false,
      focusedPane: 'primary',
      activeTabId: nextActive
    };
  }

  if (primary.length === 0) {
    const nextTabs = tabs.map((tab) => ({ ...tab, pane: 'primary' as const }));
    const nextActive =
      (activeTabId && nextTabs.some((tab) => tab.id === activeTabId) ? activeTabId : null) ??
      nextTabs[0]?.id ??
      null;
    return {
      tabs: nextTabs,
      split: false,
      focusedPane: 'primary',
      activeTabId: nextActive
    };
  }

  const nextActive =
    (activeTabId && tabs.some((tab) => tab.id === activeTabId) ? activeTabId : null) ??
    primary[0]?.id ??
    secondary[0]?.id ??
    null;
  const focused = tabs.find((tab) => tab.id === nextActive)?.pane ?? 'primary';
  return { tabs, split: true, focusedPane: focused, activeTabId: nextActive };
}

export const useEditorWorkspaceStore = create<EditorWorkspaceState>((set, get) => ({
  ...INITIAL_EDITOR_WORKSPACE_STATE,

  openTab: (input) => {
    const id = buildEditorTabId(input.kind, input.mountId, input.path);
    const title = input.title ?? defaultTabTitle(input.path);
    const existing = get().tabs.find((tab) => tab.id === id);

    if (existing) {
      set((state) => ({
        tabs: state.tabs.map((tab) =>
          tab.id === id
            ? {
                ...tab,
                accessMode: input.accessMode ?? tab.accessMode,
                mediaKind: input.mediaKind ?? tab.mediaKind,
                title
              }
            : tab
        ),
        activeTabId: id,
        focusedPane: existing.pane
      }));
      return id;
    }

    const { split, focusedPane } = get();
    const pane = split ? focusedPane : 'primary';
    const tab: EditorTab = {
      id,
      kind: input.kind,
      mountId: input.mountId,
      path: input.path,
      title,
      dirty: false,
      pane,
      accessMode: input.accessMode ?? 'edit',
      mediaKind: input.mediaKind
    };

    set((state) => ({
      tabs: [...state.tabs, tab],
      activeTabId: id,
      focusedPane: pane
    }));
    return id;
  },

  focusTab: (tabId) => {
    const tab = get().tabs.find((item) => item.id === tabId);
    if (!tab) return;
    set({ activeTabId: tabId, focusedPane: tab.pane });
  },

  focusPane: (pane) => {
    if (!get().split && pane === 'secondary') return;
    set({ focusedPane: pane });
  },

  closeTab: (tabId) => {
    const { tabs, activeTabId, split } = get();
    if (!tabs.some((tab) => tab.id === tabId)) return activeTabId;

    const nextActiveCandidate =
      activeTabId === tabId ? neighborTabId(tabs, tabId) : activeTabId;
    const nextTabs = tabs.filter((tab) => tab.id !== tabId);
    const collapsed = collapseEmptyPanes(nextTabs, nextActiveCandidate, split);
    set(collapsed);
    return collapsed.activeTabId;
  },

  closeOthers: (tabId) => {
    const { tabs, split } = get();
    const keep = tabs.find((tab) => tab.id === tabId);
    if (!keep) return get().activeTabId;
    const nextTabs = [{ ...keep, pane: 'primary' as const }];
    const collapsed = collapseEmptyPanes(nextTabs, tabId, split);
    set(collapsed);
    return collapsed.activeTabId;
  },

  closeToTheRight: (tabId) => {
    const { tabs, activeTabId, split } = get();
    const tab = tabs.find((item) => item.id === tabId);
    if (!tab) return activeTabId;
    const same = tabsInPane(tabs, tab.pane);
    const index = same.findIndex((item) => item.id === tabId);
    if (index < 0) return activeTabId;
    const closeIds = new Set(same.slice(index + 1).map((item) => item.id));
    const nextTabs = tabs.filter((item) => !closeIds.has(item.id));
    const nextActive = closeIds.has(activeTabId ?? '') ? tabId : activeTabId;
    const collapsed = collapseEmptyPanes(nextTabs, nextActive, split);
    set(collapsed);
    return collapsed.activeTabId;
  },

  closeAll: () => {
    set({ ...INITIAL_EDITOR_WORKSPACE_STATE });
    return null;
  },

  splitRight: (tabId) => {
    const { tabs } = get();
    const tab = tabs.find((item) => item.id === tabId);
    if (!tab) return;
    const same = tabsInPane(tabs, tab.pane);
    const canMove = same.length > 1;
    const nextTabs = tabs.map((item) => {
      if (item.id !== tabId) return { ...item, pane: 'primary' as const };
      return { ...item, pane: canMove ? ('secondary' as const) : ('primary' as const) };
    });
    const moved = nextTabs.find((item) => item.id === tabId);
    set({
      tabs: nextTabs,
      split: true,
      activeTabId: tabId,
      focusedPane: moved?.pane ?? 'primary'
    });
  },

  moveToPane: (tabId, pane) => {
    const { tabs, split } = get();
    if (!tabs.some((tab) => tab.id === tabId)) return;
    const nextSplit = pane === 'secondary' ? true : split;
    const nextTabs = tabs.map((tab) => (tab.id === tabId ? { ...tab, pane } : tab));
    const collapsed = collapseEmptyPanes(nextTabs, tabId, nextSplit);
    set(collapsed);
  },

  unsplit: () => {
    const { tabs, activeTabId } = get();
    const collapsed = collapseEmptyPanes(
      tabs.map((tab) => ({ ...tab, pane: 'primary' as const })),
      activeTabId,
      false
    );
    set(collapsed);
  },

  setTabDirty: (tabId, dirty) => {
    set((state) => ({
      tabs: state.tabs.map((tab) => (tab.id === tabId ? { ...tab, dirty } : tab))
    }));
  }
}));
